import { Record as record, List } from 'immutable';
import _ from 'lodash';
import joi from 'joi';
import moment from 'moment';
import { v1 as uuidv1 } from 'uuid';

import config from '../../../../src/config';
import validators from '../../../../src/validation';
import ValidationMixin from '../../../../src/records/ValidationMixin';
import { getDefaults, getResolvers } from '../../../../src/helpers/Records';
import {
    construct,
    fields,
    resolve,
    resolveAll
} from '../../../../src/helpers/LinkMapper';

import Station from '../../../../src/records/Station';
import IBlocksEligibilityResponse, { IBlocksRoute } from './IBlocksEligibility';
import SeasonTicket from './SeasonTicket';

const fieldSchema = {
    internalId: [fields.string],
    origin: [resolve.as(Station)],
    destination: [resolve.as(Station)],
    routeCode: [resolve.as(IBlocksRoute)],
    tickets: [resolveAll.as(SeasonTicket), new List()],
    eligibility: [resolve.as(IBlocksEligibilityResponse)],
    routeOnly: [fields.bool, false]
};

class CompensationRoute extends record(getDefaults(fieldSchema)) {
    static fromJSON(json = {}) {
        return construct(CompensationRoute, json, getResolvers(fieldSchema));
    }

    constructor(values = {}) {
        super(_.defaults({}, values, { internalId: uuidv1() }));
    }

    getComputedValues() {
        return {
            origin: this.getIn(['origin', 'nlc']),
            destination: this.getIn(['destination', 'nlc']),
            ticketCount: this.tickets.size,
            tickets: this.tickets.every(tkt => tkt.isComplete()),
            isEligible: this.isEligible()
        };
    }

    getSchema() {
        return {
            origin: validators.requiredString(),
            destination: validators.requiredString(),
            routeCode: validators.optional(),
            routeDescription: validators.optional(),
            ticketCount: this.routeOnly ? joi.any() : joi.number().min(1),
            tickets: validators.true(),
            isEligible: validators.true()
        };
    }

    getDescriptor() {
        const originNlc = _.get(this, 'origin.nlc');
        const destinationNlc = _.get(this, 'destination.nlc');
        const routeCode = _.get(this, 'routeCode.code');
        return [originNlc, destinationNlc, routeCode].join(':');
    }

    canSearch() {
        return (
            this.origin &&
            !this.origin.isEmpty() &&
            this.destination &&
            !this.destination.isEmpty()
        );
    }

    isEmpty() {
        return (
            (!this.origin || this.origin.isEmpty()) &&
            (!this.destination || this.destination.isEmpty())
        );
    }

    getRouteCode() {
        return this.getIn(['routeCode', 'code']);
    }

    isEligible() {
        const { eligibility } = this;
        return eligibility && eligibility.isEligible(this.getRouteCode());
    }

    isLevel1() {
        const { eligibility } = this;
        return eligibility && eligibility.isLevel1(this.getRouteCode());
    }

    isLevel2() {
        const { eligibility } = this;
        return eligibility && eligibility.isLevel2(this.getRouteCode());
    }

    getCompensationLevel() {
        const { eligibility } = this;
        return (
            eligibility && eligibility.getCompensationLevel(this.getRouteCode())
        );
    }

    isComplementary(route) {
        return (
            route &&
            route.canSearch() &&
            this.canSearch() &&
            this.origin.nlc === route.destination.nlc &&
            this.destination.nlc === route.origin.nlc
        );
    }

    canAddTicket() {
        return (
            this.isEligible() &&
            this.tickets.size < config.get('compensationMaxTicketsPerRoute')
        );
    }

    addTicket() {
        const { tickets } = this;
        const { photocardNumber } = this.customer;
        return this.set(
            'tickets',
            tickets.push(new SeasonTicket({ photocardNumber }))
        );
    }

    removeTicket(ticket) {
        const tickets = this.tickets.filterNot(
            t => t.internalId === ticket.internalId
        );
        return this.set('tickets', tickets);
    }

    updateTicket(ticket, updater) {
        const index = this.tickets.findIndex(
            t => t.internalId === ticket.internalId
        );
        if (index >= 0) {
            const updated = updater(this.tickets.get(index));
            return this.setIn(['tickets', index], updated);
        }
        return this;
    }

    getReturnRoute() {
        return this.merge({
            internalId: uuidv1(),
            origin: this.destination,
            destination: this.origin,
            routeCode: this.routeCode,
            eligibility: this.eligibility
        });
    }

    get eligibleDuration() {
        const tickets = this.tickets || new List();
        return tickets.reduce(
            (duration, ticket) => duration.add(ticket.eligibleDuration),
            moment.duration()
        );
    }

    serializeClaims() {
        const { routeOnly, origin, destination, routeCode, tickets } = this;

        if (routeOnly) {
            return new List();
        }

        return tickets.map(tkt =>
            tkt.asClaim({ origin, destination, routeCode })
        );
    }
}

Object.assign(CompensationRoute.prototype, ValidationMixin);

export default CompensationRoute;
